import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import { promptLogin } from '../utils/requireLogin';

/**
 * Shared oninit/onupdate/load skeleton for SocietyPage, ExhibitionPage, and
 * CartothequePage - each is a "load one main resource by the route's :id,
 * reload if that id changes" page, differing only in what gets fetched and
 * how the result populates page-specific fields. `error`/`requiresLogin`
 * are owned entirely by this class - every concrete page treated them
 * identically (skip the doomed authenticated request as a guest, a
 * catch-all sets `error` on any fetch failure).
 *
 * A subclass's own state resets are split into two hooks rather than one,
 * matching a real (and apparently deliberate) asymmetry already present in
 * every one of these pages before this class existed: UI-only state
 * (search text, filters, the cartothèque range slider's bounds, ...) was
 * only ever reset once, in oninit() - navigating from one society's/
 * exhibition's page to another's (an onupdate()-triggered reload) left
 * that UI state exactly as the user had it, only the fetched data itself
 * got cleared and re-fetched. resetOnCreate()/resetData() preserve that
 * split intentionally - collapsing them into one would reset a user's
 * search/filter every time they navigate between two of a société's
 * pages, which the original code never did.
 *
 * Subclasses must implement:
 * - resetData(): reset every field fetch()/onLoaded() populate, to their
 *   initial (loading) value. Called from load() every time, including via
 *   oninit()'s own call to it.
 * - currentId(): the id of whatever's currently loaded (e.g.
 *   `this.society && this.society.id()`), compared against the route's
 *   own :id param to decide whether to reload. Must be null/undefined
 *   while nothing's loaded yet, so onupdate() never reloads before the
 *   first load() has completed.
 * - fetch(): returns the Promise load() awaits; whatever it resolves to is
 *   passed to onLoaded().
 * - onLoaded(result): populate page-specific fields from `result`, call
 *   app.setTitle(...), and m.redraw() - same as each page's own .then()
 *   body did before this class existed.
 *
 * Override resetOnCreate() only if the page has UI-only state beyond
 * data/error/requiresLogin (SocietyPage doesn't; ExhibitionPage and
 * CartothequePage do) - default is a no-op.
 */
export default class AutoReloadingPage extends Page {
  error = false;
  requiresLogin = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.error = false;
    this.requiresLogin = false;
    this.resetOnCreate();
    if (!app.session.user) {
      promptLogin();
    }
    this.load();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    const currentId = this.currentId();
    if (currentId != null && String(currentId) !== m.route.param('id')) {
      this.load();
    }
  }
  load() {
    this.error = false;
    this.resetData();
    if (!app.session.user) {
      // The underlying resource(s) are all ->authenticated() - skip the
      // doomed request rather than showing the generic error (see
      // requireLogin.js's own docblock).
      this.requiresLogin = true;
      return;
    }
    this.requiresLogin = false;
    this.fetch().then(result => this.onLoaded(result)).catch(() => {
      this.error = true;
      m.redraw();
    });
  }
  resetOnCreate() {}
  resetData() {
    throw new Error('AutoReloadingPage subclasses must implement resetData()');
  }
  currentId() {
    throw new Error('AutoReloadingPage subclasses must implement currentId()');
  }
  fetch() {
    throw new Error('AutoReloadingPage subclasses must implement fetch()');
  }
  onLoaded(_result) {
    throw new Error('AutoReloadingPage subclasses must implement onLoaded()');
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/AutoReloadingPage', AutoReloadingPage);